import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["banner", "settingsModal", "necessarySwitch", "analyticsSwitch", "marketingSwitch"]
  static values = {
    gtag: String,
    requiresConsent: Boolean
  }

  connect() {
    this.initializeCookieConsent()

    // Listen for global events
    window.addEventListener('showCookieSettings', this.handleShowSettings.bind(this))
  }

  disconnect() {
    window.removeEventListener('showCookieSettings', this.handleShowSettings.bind(this))
  }

  initializeCookieConsent() {
    // Check if user needs to see consent (based on geo-location or previous consent)
    if (this.requiresConsentValue && !this.hasExistingConsent()) {
      this.showBanner()
    } else if (this.hasExistingConsent()) {
      this.loadConsentedServices()
    } else {
      // For non-EU users, load all services by default but still provide opt-out
      this.setDefaultConsent()
      this.loadConsentedServices()
    }
  }

  hasExistingConsent() {
    return localStorage.getItem('cookie-consent') !== null
  }

  getConsentData() {
    const defaultConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }

    const stored = localStorage.getItem('cookie-consent')
    return stored ? JSON.parse(stored) : defaultConsent
  }

  setDefaultConsent() {
    // For non-EU users, opt-in by default but allow opt-out
    const consent = {
      necessary: true,
      analytics: true,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }
    localStorage.setItem('cookie-consent', JSON.stringify(consent))
  }

  saveConsent(consentData) {
    localStorage.setItem('cookie-consent', JSON.stringify({
      ...consentData,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }))

    // Trigger consent change event
    window.dispatchEvent(new CustomEvent('consentChanged', {
      detail: consentData
    }))
  }

  loadConsentedServices() {
    const consent = this.getConsentData()
    console.log('Loading consented services. Consent:', consent, 'Gtag Value:', this.gtagValue)

    if (consent.analytics && this.gtagValue) {
      this.loadGoogleAnalytics()
    }

    if (consent.marketing) {
      this.loadMarketingServices()
    }

    // Update UI if settings modal is present
    this.updateConsentSwitches(consent)
  }

  loadGoogleAnalytics() {
    // Only load if not already loaded
    if (!window.gtag) {
      console.log('Loading Google Analytics with ID:', this.gtagValue)

      // Initialize dataLayer before loading script
      window.dataLayer = window.dataLayer || []
      window.gtag = function () {
        window.dataLayer.push(arguments)
      }

      // Load the Google Analytics script
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gtagValue}`
      document.head.appendChild(script)

      script.onload = () => {
        // Configure Google Analytics after script loads
        window.gtag('js', new Date())
        window.gtag('config', this.gtagValue, {
          anonymize_ip: true,
          allow_google_signals: false,
          allow_ad_personalization_signals: false
        })
        console.log('Google Analytics loaded successfully')
      }

      script.onerror = () => {
        console.error('Failed to load Google Analytics script')
      }
    } else {
      console.log('Google Analytics already loaded')
    }
  }

  loadMarketingServices() {
    // Add other marketing/advertising scripts here when needed
    console.log('Marketing services would be loaded here')
  }

  updateConsentSwitches(consent) {
    if (this.hasNecessarySwitchTarget) {
      this.necessarySwitchTarget.checked = consent.necessary
    }
    if (this.hasAnalyticsSwitchTarget) {
      this.analyticsSwitchTarget.checked = consent.analytics
    }
    if (this.hasMarketingSwitchTarget) {
      this.marketingSwitchTarget.checked = consent.marketing
    }
  }

  // Banner Actions
  showBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove('hidden')
      this.bannerTarget.classList.add('show')
    }
  }

  hideBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove('show')
      this.bannerTarget.classList.add('hidden')
    }
  }

  acceptAll() {
    console.log('Accept All clicked')
    const consent = {
      necessary: true,
      analytics: true,
      marketing: false
    }

    this.saveConsent(consent)
    this.loadConsentedServices()
    this.hideBanner()
  } acceptNecessary() {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false
    }

    this.saveConsent(consent)
    this.hideBanner()
  }

  // Settings Modal Actions
  showSettings() {
    if (this.hasSettingsModalTarget) {
      const consent = this.getConsentData()
      this.updateConsentSwitches(consent)
      this.settingsModalTarget.classList.remove('hidden')
      this.settingsModalTarget.classList.add('show')
    }
  }

  hideSettings() {
    if (this.hasSettingsModalTarget) {
      this.settingsModalTarget.classList.remove('show')
      this.settingsModalTarget.classList.add('hidden')
    }
  }

  // Handle external events (like from footer button)
  handleShowSettings(event) {
    this.showSettings()
  }

  saveSettings() {
    const consent = {
      necessary: this.hasNecessarySwitchTarget ? this.necessarySwitchTarget.checked : true,
      analytics: this.hasAnalyticsSwitchTarget ? this.analyticsSwitchTarget.checked : false,
      marketing: this.hasMarketingSwitchTarget ? this.marketingSwitchTarget.checked : false
    }

    this.saveConsent(consent)
    this.hideSettings()

    // Reload page to apply new consent settings
    window.location.reload()
  }

  // Cookie Management
  clearAllCookies() {
    // Clear all non-necessary cookies
    const cookies = document.cookie.split(";")

    for (let cookie of cookies) {
      const eqPos = cookie.indexOf("=")
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()

      // Don't clear necessary cookies
      if (!this.isNecessaryCookie(name)) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`
      }
    }

    // Clear localStorage items (except consent)
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key !== 'cookie-consent') {
        localStorage.removeItem(key)
      }
    }

    // Clear sessionStorage
    sessionStorage.clear()
  }

  isNecessaryCookie(name) {
    const necessaryCookies = [
      '_session',
      'csrf_token',
      'flash',
      'cookie-consent'
    ]

    return necessaryCookies.some(necessary => name.includes(necessary))
  }

  // Geo-location detection (simplified)
  static detectRequiresConsent() {
    // This would typically be done server-side with IP geolocation
    // For now, we'll use a simple heuristic based on timezone/language
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const language = navigator.language

    // EU timezones and languages that require explicit consent
    const euRegions = [
      'Europe/', 'GMT', 'WET', 'CET', 'EET',
      'Atlantic/Azores', 'Atlantic/Madeira', 'Atlantic/Canary'
    ]

    const euLanguages = [
      'de', 'fr', 'it', 'es', 'nl', 'pl', 'ro', 'el', 'pt', 'cs', 'hu', 'sv',
      'da', 'fi', 'sk', 'hr', 'bg', 'sl', 'et', 'lv', 'lt', 'mt', 'ga'
    ]

    const isEuTimezone = euRegions.some(region => timezone.includes(region))
    const isEuLanguage = euLanguages.some(lang => language.startsWith(lang))

    return isEuTimezone || isEuLanguage
  }
}